import { maskEmail } from '@/utils/auth/helpers';
import {
  COGNITO_ATTR_PHONE_NUMBER_VERIFIED,
  COGNITO_ATTR_PREFERRED_MFA_METHOD,
  MFA_METHOD_EMAIL,
  MFA_METHOD_SMS,
  MFA_METHOD_TOTP,
} from '@/utils/constants/mfa';
import useGet from '@/utils/hooks/api/vercel/useGet';
import usePost from '@/utils/hooks/api/typescript/usePost';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Hook for managing MFA method selection during login.
 * Supports preferred method: if user has a preferred method set and available,
 * it will be used automatically. Otherwise, shows method selection screen.
 * Supports email, SMS, and TOTP MFA methods.
 *
 * @param {Object} user - Cognito user object with attributes
 * @returns {Object} MFA state and handlers including hasPhone, hasTotp, availableMethods, etc.
 */
export default function useMfa(user) {
  // Store initial challengeParam data to preserve it across challenge responses
  // This ensures method selection screen always has maskedEmail, maskedPhone, hasPhone, etc.
  const initialChallengeParamRef = useRef(null);
  const previousUsernameRef = useRef(null);

  // Reset ref when user changes (new login session)
  useEffect(() => {
    const currentUsername = user?.username;
    if (currentUsername !== previousUsernameRef.current) {
      initialChallengeParamRef.current = null;
      previousUsernameRef.current = currentUsername;
      // Reset selectedMethod when user changes to prevent state from persisting across sessions
      setSelectedMethod(MFA_METHOD_EMAIL);
    }
  }, [user?.username]);

  // Store initial challengeParam when first received (has all method selection data)
  useEffect(() => {
    if (user?.challengeParam && !initialChallengeParamRef.current) {
      initialChallengeParamRef.current = { ...user.challengeParam };
    }
  }, [user?.challengeParam]);

  // Merge current and initial challengeParam
  // Prioritize current values but fall back to initial values if current is missing data
  const mergedChallengeParam = useMemo(() => {
    const current = user?.challengeParam || {};
    const initial = initialChallengeParamRef.current || {};
    return {
      ...initial, // Start with initial values
      ...current, // Override with current values (if any)
    };
  }, [user?.challengeParam]);

  // Get preferred method from challengeParam (set by createAuth Lambda) or user attributes (fallback)
  // Backend validates that preferred method is available (e.g., 'sms' requires verified phone)
  // Fallback to user attributes when challengeParam is missing (e.g., after OTP is sent)
  const preferredMethodFromChallenge = mergedChallengeParam?.preferredMethod;
  const preferredMethodFromAttributes =
    user?.attributes?.[COGNITO_ATTR_PREFERRED_MFA_METHOD];
  const preferredMethod =
    preferredMethodFromChallenge || preferredMethodFromAttributes || null;

  /**
   * Parse availableMethods string/array into an array of method strings.
   * @param {string | string[] | undefined} availableMethodsStr - Methods as string or array
   * @returns {string[]} Array of method strings
   */
  const parseAvailableMethods = useMemo(() => {
    return (availableMethodsStr) => {
      if (!availableMethodsStr) {
        return [];
      }
      return typeof availableMethodsStr === 'string'
        ? availableMethodsStr.split(',').filter(Boolean)
        : Array.isArray(availableMethodsStr)
        ? availableMethodsStr
        : [];
    };
  }, []);

  // Use backend's data to determine if SMS is available
  // Backend provides hasPhone in challengeParam, or we can check user attributes
  // Fallback to user attributes when challengeParam is missing (e.g., after OTP is sent)
  const hasPhone = useMemo(() => {
    // First, check if backend explicitly provided hasPhone (from merged challengeParam)
    if (mergedChallengeParam?.hasPhone !== undefined) {
      return (
        mergedChallengeParam.hasPhone === 'true' ||
        mergedChallengeParam.hasPhone === true
      );
    }
    // Fallback: check user attributes for phone verification
    const phoneVerifiedFromAttributes =
      user?.attributes?.[COGNITO_ATTR_PHONE_NUMBER_VERIFIED];
    if (phoneVerifiedFromAttributes !== undefined) {
      return (
        phoneVerifiedFromAttributes === 'true' ||
        phoneVerifiedFromAttributes === 'True' ||
        phoneVerifiedFromAttributes === true ||
        phoneVerifiedFromAttributes === '1' ||
        phoneVerifiedFromAttributes === 1
      );
    }
    // Last resort: check if availableMethods from challengeParam includes 'sms'
    const methods = parseAvailableMethods(
      mergedChallengeParam?.availableMethods
    );
    return methods.includes('sms');
  }, [
    mergedChallengeParam?.hasPhone,
    user?.attributes?.[COGNITO_ATTR_PHONE_NUMBER_VERIFIED],
    mergedChallengeParam?.availableMethods,
    parseAvailableMethods,
  ]);

  // Use backend's data to determine if TOTP is available
  // Backend provides hasTotp in challengeParam, or we can check availableMethods
  const hasTotp = useMemo(() => {
    // First, check if backend explicitly provided hasTotp (from merged challengeParam)
    if (mergedChallengeParam?.hasTotp !== undefined) {
      return (
        String(mergedChallengeParam.hasTotp).toLowerCase() === 'true' ||
        String(mergedChallengeParam.hasTotp) === '1'
      );
    }
    // Fallback: check if availableMethods from challengeParam includes 'totp'
    const methods = parseAvailableMethods(
      mergedChallengeParam?.availableMethods
    );
    return methods.includes('totp');
  }, [
    mergedChallengeParam?.hasTotp,
    mergedChallengeParam?.availableMethods,
    parseAvailableMethods,
  ]);

  // Backend returns availableMethods as comma-separated string, convert to array
  // If challengeParam.availableMethods is missing, derive from hasPhone and hasTotp
  const availableMethods = useMemo(() => {
    const availableMethodsStr = mergedChallengeParam?.availableMethods;
    if (availableMethodsStr) {
      const parsed = parseAvailableMethods(availableMethodsStr);
      return parsed.length > 0 ? parsed : ['email'];
    }
    // If challengeParam.availableMethods is missing, derive from hasPhone and hasTotp
    const methods = ['email'];
    if (hasPhone) {
      methods.push('sms');
    }
    if (hasTotp) {
      methods.push('totp');
    }
    return methods;
  }, [
    mergedChallengeParam?.availableMethods,
    hasPhone,
    hasTotp,
    parseAvailableMethods,
  ]);

  // Check if preferred method is valid and available
  // Note: availableMethods might be ['email'] if challengeParam is missing, so we also check hasPhone and hasTotp
  // to determine if SMS/TOTP should be available
  const hasPreferredMethod = useMemo(() => {
    if (!preferredMethod) {
      return false;
    }
    // If preferred method is 'sms', must have verified phone
    if (preferredMethod === MFA_METHOD_SMS && !hasPhone) {
      return false;
    }
    // If preferred method is 'totp', must have TOTP enabled
    if (preferredMethod === MFA_METHOD_TOTP && !hasTotp) {
      return false;
    }
    // Preferred method must be in available methods, OR if challengeParam is missing,
    // we can still validate based on the preferred method, hasPhone, and hasTotp
    if (availableMethods.includes(preferredMethod)) {
      return true;
    }
    // If availableMethods doesn't include the preferred method but we have it from attributes,
    // and it's valid (email is always valid, sms requires hasPhone, totp requires hasTotp),
    // then it's still valid
    if (
      preferredMethodFromAttributes &&
      (preferredMethod === MFA_METHOD_EMAIL ||
        (preferredMethod === MFA_METHOD_SMS && hasPhone) ||
        (preferredMethod === MFA_METHOD_TOTP && hasTotp))
    ) {
      return true;
    }
    return false;
  }, [
    preferredMethod,
    preferredMethodFromAttributes,
    availableMethods,
    hasPhone,
    hasTotp,
  ]);

  // Set selected method: use preferred method if available, otherwise default to email
  const [selectedMethod, setSelectedMethod] = useState(() =>
    hasPreferredMethod ? preferredMethod : MFA_METHOD_EMAIL
  );

  // Update selected method when preferred method becomes available
  // Reset to email if preferred method becomes unavailable
  // Only run when hasPreferredMethod or preferredMethod changes, not when selectedMethod changes
  useEffect(() => {
    if (hasPreferredMethod && preferredMethod) {
      setSelectedMethod(preferredMethod);
    } else if (!hasPreferredMethod) {
      // Reset to email when preferred method is not available
      setSelectedMethod(MFA_METHOD_EMAIL);
    }
  }, [hasPreferredMethod, preferredMethod]);

  // Memoize masked values to avoid recalculating on every render
  // Use values from backend (already masked)
  const maskedEmail = useMemo(() => {
    if (mergedChallengeParam?.maskedEmail) {
      return mergedChallengeParam.maskedEmail;
    }
    // If backend didn't provide maskedEmail, mask it from user attributes
    const unmaskedEmail = user?.attributes?.email;
    return unmaskedEmail ? maskEmail(unmaskedEmail) : '';
  }, [mergedChallengeParam?.maskedEmail, user?.attributes?.email]);

  const maskedPhone = useMemo(() => {
    // Backend returns maskedPhone - use it directly
    if (mergedChallengeParam?.maskedPhone) {
      return mergedChallengeParam.maskedPhone;
    }
    // If backend didn't provide maskedPhone, return empty
    return '';
  }, [mergedChallengeParam?.maskedPhone]);

  const {
    fire: startMfaChallenge,
    isLoading: isStartingChallenge,
    error: startChallengeError,
    data: startChallengeData,
    query: startChallengeQuery,
  } = useGet('/auth/start-mfa-challenge', { fireOnMount: false });

  const {
    fire: resendOtp,
    isLoading: resendLoading,
    error: resendError,
    data: resendData,
  } = usePost('/auth/resend-otp', { fireOnMount: false });

  return {
    selectedMethod,
    setSelectedMethod,
    hasPhone,
    hasTotp,
    maskedEmail,
    maskedPhone,
    preferredMethod,
    hasPreferredMethod,
    availableMethods,
    startMfaChallenge,
    isStartingChallenge,
    startChallengeError,
    startChallengeData,
    resendOtp,
    resendLoading,
    resendError,
    resendData,
    startChallengeQuery,
  };
}
